/**
 * Profile Validation Utilities
 * 
 * Centralized validation for user profile mandatory fields.
 * Used for job application eligibility checks and form validation.
 */

// Mandatory fields required for job application eligibility
export const MANDATORY_PERSONAL_FIELDS = {
    first_name: 'First Name',
    last_name: 'Last Name',
    date_of_birth: 'Date of Birth',
    address_line_1: 'Address',
    city: 'City',
    state: 'State',
    country: 'Country',
    mobile_number: 'Phone Number',
    profile_picture_url: 'Profile Picture',
};

/**
 * Get list of incomplete mandatory personal fields
 * @param {Object} user - User object with profile data
 * @returns {Array<{field: string, label: string}>} Array of incomplete field info
 */
export const getIncompletePersonalFields = (user) => {
    if (!user) {
        return Object.entries(MANDATORY_PERSONAL_FIELDS).map(([field, label]) => ({
            field,
            label,
        }));
    }

    const incompleteFields = [];
    
    for (const [field, label] of Object.entries(MANDATORY_PERSONAL_FIELDS)) {
        const value = user[field];
        if (!value || (typeof value === 'string' && !value.trim())) {
            incompleteFields.push({ field, label });
        }
    }
    
    return incompleteFields;
};

/**
 * Check if user has completed all mandatory personal info
 * @param {Object} user - User object with profile data
 * @returns {boolean} True if all mandatory fields are filled
 */
export const hasCompletedPersonalInfo = (user) => {
    return getIncompletePersonalFields(user).length === 0;
};

// ==================== SECTION-SPECIFIC VALIDATION ====================

/**
 * Validate personal info for ResumeTab save
 * @param {Object} formData - Form data object
 * @returns {Object} Errors object with field -> message mapping
 */
export const validatePersonalInfoForSave = (formData) => {
    const errors = {};
    if (!formData.first_name?.trim()) {
        errors.first_name = 'First Name is required';
    }
    if (!formData.last_name?.trim()) {
        errors.last_name = 'Last Name is required';
    }
    if (!formData.mobile_number?.trim()) {
        errors.mobile_number = 'Phone Number is required';
    }
    if (!formData.date_of_birth?.trim()) {
        errors.date_of_birth = 'Date of Birth is required';
    }
    return errors;
};

/**
 * Validate education entries
 * @param {Array} educationList - List of education entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateEducationEntries = (educationList) => {
    const errors = [];
    educationList.forEach((edu, index) => {
        const entryErrors = {};
        if (!edu.school?.trim() && !edu.institution?.trim()) {
            entryErrors.school = 'School is required';
        }
        if (!edu.degree?.trim()) {
            entryErrors.degree = 'Degree is required';
        }
        if (!edu.start_date?.trim()) {
            entryErrors.start_date = 'Start Date is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate experience entries
 * @param {Array} experienceList - List of experience entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateExperienceEntries = (experienceList) => {
    const errors = [];
    experienceList.forEach((exp, index) => {
        const entryErrors = {};
        if (!exp.company?.trim()) {
            entryErrors.company = 'Company is required';
        }
        if (!exp.role?.trim() && !exp.title?.trim()) {
            entryErrors.role = 'Role is required';
        }
        if (!exp.start_date?.trim()) {
            entryErrors.start_date = 'Start Date is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate project entries
 * @param {Array} projectsList - List of project entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateProjectEntries = (projectsList) => {
    const errors = [];
    projectsList.forEach((proj, index) => {
        const entryErrors = {};
        if (!proj.name?.trim()) {
            entryErrors.name = 'Project Name is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate publication entries
 * @param {Array} publicationsList - List of publication entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validatePublicationEntries = (publicationsList) => {
    const errors = [];
    publicationsList.forEach((pub, index) => {
        const entryErrors = {};
        if (!pub.title?.trim()) {
            entryErrors.title = 'Title is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate certification entries
 * @param {Array} certificationsList - List of certification entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateCertificationEntries = (certificationsList) => {
    const errors = [];
    certificationsList.forEach((cert, index) => {
        const entryErrors = {};
        if (!cert.name?.trim()) {
            entryErrors.name = 'Certification Name is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate award entries
 * @param {Array} awardsList - List of award entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateAwardEntries = (awardsList) => {
    const errors = [];
    awardsList.forEach((award, index) => {
        const entryErrors = {};
        if (!award.name?.trim()) {
            entryErrors.name = 'Award Name is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate coding profile entries
 * @param {Array} profilesList - List of coding profile entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateCodingProfileEntries = (profilesList) => {
    const errors = [];
    profilesList.forEach((profile, index) => {
        const entryErrors = {};
        if (!profile.username?.trim()) {
            entryErrors.username = 'Username is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

/**
 * Validate link entries
 * @param {Array} linksList - List of link entries
 * @returns {Array<{index: number, errors: Object}>} Validation errors per entry
 */
export const validateLinkEntries = (linksList) => {
    const errors = [];
    linksList.forEach((link, index) => {
        const entryErrors = {};
        if (!link.url?.trim()) {
            entryErrors.url = 'URL is required';
        }
        if (Object.keys(entryErrors).length > 0) {
            errors.push({ index, ...entryErrors });
        }
    });
    return errors;
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Format validation errors into user-friendly message
 * @param {Array} errors - Array of validation error objects
 * @param {string} sectionName - Name of the section (e.g., "Education")
 * @returns {string} Formatted error message
 */
export const formatValidationErrors = (errors, sectionName) => {
    if (!errors || errors.length === 0) return '';
    
    const messages = errors.map(error => {
        const entryNum = error.index + 1;
        const fieldErrors = Object.entries(error)
            .filter(([key]) => key !== 'index')
            .map(([, message]) => message);
        
        if (fieldErrors.length === 1) {
            return `${sectionName} #${entryNum}: ${fieldErrors[0]}`;
        }
        return `${sectionName} #${entryNum}: ${fieldErrors.join(', ')}`;
    });
    
    return messages.join('. ');
};

/**
 * Format personal info errors into user-friendly message
 * @param {Object} errors - Object with field -> message mapping
 * @returns {string} Formatted error message
 */
export const formatPersonalInfoErrors = (errors) => {
    if (!errors || Object.keys(errors).length === 0) return '';
    return Object.values(errors).join('. ');
};
